import { isPlatformServer } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { UserService } from '~/auth/services/user/user.service';
import { CheckoutItem } from '~/checkout/model/checkout-item.model';
import { BookingService } from '~/checkout/services/booking/booking.service';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import {
  googleCalendarUrl,
  icsUrl,
  outlookUrl,
} from '~/core/helpers/calendar/calendars.helper';
import { TitleService } from '~/core/services/title/title.service';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';
import { ActivityService } from '~/core/services/activity/activity.service';
import { QrCodeBookingInterface } from '~/organizer/shared/services/qr-code/interfaces/qr-code-interface';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { WindowService } from '~/core/services/window/window.service';

export interface WalletPassResponse {
  url: string;
}

@Component({
  selector: 'app-confirmation',
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.scss'],
})
export class ConfirmationComponent implements OnInit, OnDestroy {
  private readonly mediaMethods = {
    google: googleCalendarUrl,
    outlook: outlookUrl,
    ical: icsUrl,
  };
  private readonly subscriptions = new Subscription();

  public readonly mediaList = ['google', 'ical', 'outlook'];
  public readonly paramethorNameEnum = ParamethorNameEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;

  public decatClubUrl: string;
  public items: CheckoutItem[];
  public confirmType: string;
  public qrCode: string;
  public pdfUrl: string;
  public walletUrl: string;
  public svg: string;
  public sanitizedSvg: SafeResourceUrl;
  public hasError: string;
  public isLoading = true;
  public activityEligible = false;
  public title: string;
  public bookingItemId: string;
  public isMobileDevice: boolean;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly activityService: ActivityService,
    private readonly checkoutService: CheckoutService,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    private readonly titleService: TitleService,
    private readonly paramethorService: ParamethorService,
    private readonly userService: UserService,
    private readonly translateService: TranslateService,
    private readonly bookingService: BookingService,
    private readonly sanitizer: DomSanitizer,
    private readonly matchMediaService: MatchMediaService,
    private readonly windowService: WindowService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }

  public ngOnInit(): void {
    this.titleService.setPageTitle('seo.checkout.confirmation.title');

    this.subscriptions.add(
      this.checkoutService.getItems().subscribe((checkoutItems) => {
        this.items = checkoutItems;
      })
    );

    this.subscriptions.add(
      this.activatedRoute?.queryParamMap?.subscribe((params) => {
        if (params.get('bookingItemId')) {
          this.bookingItemId = params.get('bookingItemId');
          const bookingItemId = params.get('bookingItemId');

          this.bookingService.getBookingItems(bookingItemId).subscribe({
            next: (item) => {
              this.activityService
                .getActivity(item.sku.split('-')[0])
                .subscribe({
                  next: (activity) => {
                    if (activity.type === 'EVENT' && !this.items) {
                      this.activityEligible = true;
                    }
                  },
                });
            },
            error: (err) => {
              this.activityEligible = true;
              this.hasError = this.translateService.instant(
                'checkout.confirmation.qr-codes.error'
              );
            },
          });

          this.bookingService.getQrCode(bookingItemId).subscribe({
            next: (item: QrCodeBookingInterface) => {
              this.pdfUrl = item.pdfUrl;
              this.title = item.title;
              this.sanitizedSvg = this.sanitizer.bypassSecurityTrustHtml(
                item.svg
              );
            },
            complete: () => {
              this.isLoading = false;
            },
            error: (err) => {
              this.hasError = this.translateService.instant(
                'checkout.confirmation.qr-codes.error'
              );
              this.isLoading = false;
            },
          });
        }
      })
    );

    this.subscriptions.add(
      this.activatedRoute.data.subscribe((data) => {
        this.confirmType = data.type;
        this.isLoading = false;
      })
    );

    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );

    this.checkoutService.emptyCart();

    // reset cart
    this.checkoutService.resetCheckout();

    // update the user with the contact phone number data asked at the subscription step
    this.subscriptions.add(
      this.userService
        .updateUser({
          ...this.userService.getUser(),
          ...this.checkoutService.contactPhoneNumberData,
        })
        .subscribe()
    );

    this.decatClubUrl = this.paramethorService.get(
      ParamethorNameEnum.DecatClubUrl,
      null
    );
  }

  public downloadWalletPass(bookingItemId: string) {
    if (this.matchMediaService.isAppleDevice()) {
      this.bookingService.getAppleWalletPass(bookingItemId).subscribe({
        next: (data: Blob) => {
          const url = URL.createObjectURL(data);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = 'wallet-pass.pkpass';
          anchor.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          console.error('Error for wallet download');
        },
      });
    } else if (this.matchMediaService.isAndroidDevice()) {
      this.bookingService.getGoogleWalletPass(bookingItemId).subscribe({
        next: (data: WalletPassResponse) => {
          this.windowService.openNewTab(data.url);
        },
        error: () => {
          console.error('Error for wallet download');
        },
      });
    }
  }

  public get redirectPath(): string {
    if (this.confirmType === 'paying') {
      return '/attendee.bookings/attendee.waiting';
    }
    return '/attendee.homepage';
  }

  public get isActivityEvent(): boolean {
    return this.items && this.items[0]?.activity?.type === 'EVENT';
  }

  public getMediaImageSrc(media: string): string {
    return `assets/agenda/${media}.svg`;
  }

  public getMediaTitle(media: string): string {
    return this.translateService.instant('checkout.confirmation.' + media);
  }

  public redirect(media: string, item: CheckoutItem): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    window.location.href = this.mediaMethods[media](
      item.activity.name,
      item.activity.description,
      item.activity.fullAddress,
      new Date(item.timeslot.startDate),
      new Date(item.timeslot.endDate)
    );
  }
}
